import { DecoratorNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";
import { ReactNode } from "react";
import { PlaceholderComponent } from "./PlaceholderComponent";

export type PlaceholderPayload = {
  placeholder: string;
  value: string;
  mode: "add" | "edit";
  focus?: boolean;
};

export type SerializedPlaceholderNode = Spread<
  {
    placeholder: string;
    value: string;
    mode: "add" | "edit";
    version: 1;
  },
  SerializedLexicalNode
>;

export class PlaceholderNode extends DecoratorNode<ReactNode> {
  placeholderRef: React.RefObject<HTMLDivElement | null> = { current: null };

  __placeholder: string;
  __value: string;
  __mode: "add" | "edit" = "add";

  static getType(): string {
    return "placeholder";
  }

  static clone(node: PlaceholderNode): PlaceholderNode {
    return new PlaceholderNode(
      node.__placeholder,
      node.__value,
      node.__mode,
      node.__key,
    );
  }

  static importJSON(
    serializedNode: SerializedPlaceholderNode,
  ): PlaceholderNode {
    return $createPlaceholderNode({
      placeholder: serializedNode.placeholder,
      value: serializedNode.value,
      mode: serializedNode.mode,
    });
  }

  constructor(
    placeholder: string,
    value: string,
    mode: "add" | "edit",
    key?: NodeKey,
  ) {
    super(key);
    this.__placeholder = placeholder;
    this.__value = value;
    this.__mode = mode;
  }

  isIsolated(): boolean {
    return true;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "placeholder-node";
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  getTextContent(): string {
    return `{placeholder:${this.getPlaceholder()}:${this.getValue()}}`;
  }

  exportJSON(): SerializedPlaceholderNode {
    return {
      type: this.getType(),
      placeholder: this.getPlaceholder(),
      value: this.getValue(),
      mode: this.getMode(),
      version: 1,
    };
  }

  decorate(): ReactNode {
    return (
      <PlaceholderComponent
        ref={this.placeholderRef}
        placeholder={this.__placeholder}
        value={this.__value}
        nodeKey={this.getKey()}
        mode={this.__mode}
      />
    );
  }

  getValue(): string {
    return this.__value;
  }

  setValue(newValue: string) {
    const self = this.getWritable();
    self.__value = newValue;
  }

  getPlaceholder(): string {
    return this.__placeholder;
  }

  setPlaceholder(placeholder: string) {
    const self = this.getWritable();
    self.__placeholder = placeholder;
  }

  getMode(): "add" | "edit" {
    return this.__mode;
  }

  setMode(mode: "add" | "edit") {
    const self = this.getWritable();
    self.__mode = mode;
  }

  focus() {
    this.placeholderRef.current?.focus();
  }
}

export function $createPlaceholderNode(
  payload: PlaceholderPayload,
): PlaceholderNode {
  return new PlaceholderNode(payload.placeholder, payload.value, payload.mode);
}

export function $isPlaceholderNode(node: any): node is PlaceholderNode {
  return node instanceof PlaceholderNode;
}
